import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiUrlService } from '@services/api-url/api-url.service';
import { IUser } from '@interfaces/i-user';
import { IResponse } from '@interfaces/i-response';
import { ILoggedinUser } from '@interfaces/i-loggedin-user';
import { IRefreshTokenDTO } from '@interfaces/auth/i-refresh-token';
import { RSAEncryptionService } from '@helpers/rsa-encryptor';
import * as Sentry from "@sentry/angular"; import { GenericResponse } from '@interfaces/Generic/i-response';
import { LoginOtpRequest, LoginOtpResponse, LoginOtpVerifyRequest } from '@interfaces/login/otp/LoginOtp';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private apiUrlService: ApiUrlService,
    private rsaEncryptionService: RSAEncryptionService
  ) { }

  login(user: IUser): Observable<IResponse<ILoggedinUser>> {

    let encryptedPassword = this.rsaEncryptionService.encrypt(user.password);
    user.password = encryptedPassword;

    return this.http.post<IResponse<ILoggedinUser>>(
      this.apiUrlService.loginUrl,
      user
    );
  }

  logout(): Observable<IResponse<boolean>> {
    return this.http.post<IResponse<boolean>>(this.apiUrlService.logoutUrl, null);
  }

  forgotPassword(phoneOrEmail: string): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(
      this.apiUrlService.forgetPasswordUrl,
      { phoneOrEmail }
    );
  }

  verifyForgetPasswordOtp(
    phoneOrEmail: string,
    otp: string
  ): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(
      this.apiUrlService.verifyForgetPasswordOtpUrl,
      { phoneOrEmail, otp }
    );
  }

  changePassword(
    phoneOrEmail: string,
    newPassword: string
  ): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(
      this.apiUrlService.resetForgottenPasswordUrl,
      { phoneOrEmail, newPassword }
    );
  }

  getToken() {
    return localStorage.getItem('accessToken');
  }

  setAccessAndRefreshToken(accessToken: string, refreshToken: string) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  setLoginResponseInLocalStorage(data: ILoggedinUser) {
    this.setAccessAndRefreshToken(data.accessToken, data.refreshToken);
    localStorage.setItem('fullName', data.fullName);
    localStorage.setItem('imagePath', data.imagePath);
    localStorage.setItem('doctorId', data.doctorId.toString());
    localStorage.setItem('doctorTitle', data.title);
    localStorage.setItem('doctorPhone', data.phone);
    localStorage.setItem('address', data.address);
    localStorage.setItem('countryId', data.countryId?.toString() || '0');

    // Handle permissions
    const previousPermissions = localStorage.getItem('permissions');
    const currentPermissions = JSON.stringify(data.permissions || []);
    localStorage.setItem('permissions', currentPermissions);

    // Handle assistant data
    if (data.assistantId) {
      localStorage.setItem('assistantId', data.assistantId);
      const currentAssistantPermissions = JSON.stringify(data.assistantPermissions || []);
      localStorage.setItem('assistantPermissions', currentAssistantPermissions);
    }

    this.setSentryUserData(data);

    // Only reload if permissions actually CHANGED (not on first login)
    const permissionsChanged = previousPermissions && previousPermissions !== currentPermissions;
    const previousAssistantPermissions = localStorage.getItem('assistantPermissions');
    const currentAssistantPermissions = JSON.stringify(data.assistantPermissions || []);
    const assistantPermissionsChanged = previousAssistantPermissions &&
      previousAssistantPermissions !== currentAssistantPermissions;

    if (permissionsChanged || assistantPermissionsChanged) {
      window.location.reload();
    }
  }

  getAccessAndRefreshToken(): IRefreshTokenDTO {
    let tokenData: IRefreshTokenDTO = {
      accessToken: localStorage.getItem('accessToken') || '',
      refreshToken: localStorage.getItem('refreshToken') || '',
    };
    return tokenData;
  }

  refreshToken(
    tokenData: IRefreshTokenDTO
  ): Observable<IResponse<ILoggedinUser>> {
    return this.http.put<IResponse<ILoggedinUser>>(
      this.apiUrlService.refreshTokenUrl,
      tokenData
    );
  }

  logoutLocalStorageClear() {
    // Clear all auth-related items
    const itemsToRemove = [
      'accessToken',
      'refreshToken',
      'fullName',
      'imagePath',
      'doctorId',
      'assistantId',
      'doctorTitle',
      'doctorPhone',
      'address',
      'permissions',
      'assistantPermissions',
      'programs',
      'countryId',
      'personalPatientdata',
      'cities',
      'carnivitaConfigurations'
    ];

    itemsToRemove.forEach(item => localStorage.removeItem(item));
  }
  setSentryUserData(data: ILoggedinUser) {
    Sentry.setUser({ doctorId: data.doctorId, fullName: data.fullName, assistantId: data.assistantId, title: data.title });
  }

  loginViaOtp(loginOtpRequest: LoginOtpRequest): Observable<GenericResponse<LoginOtpResponse>> {
    return this.http.post<GenericResponse<LoginOtpResponse>>(this.apiUrlService.loginOtpRequestUrl, loginOtpRequest);
  }

  verifyLoginOtp(loginOtpVerifyRequest: LoginOtpVerifyRequest): Observable<GenericResponse<ILoggedinUser>> {
    return this.http.post<GenericResponse<ILoggedinUser>>(this.apiUrlService.loginOtpVerifyUrl, loginOtpVerifyRequest);
  }
}
